/**
 * BriefingTile — the AI morning briefing on the Overview home (Wave 2).
 *
 * Renders the warm Bokmål `summary` the backend generated for the current
 * member, with a small streak footer pulled from the structured facts. Leads
 * the Overview grid as the day's personal "godmorgen".
 *
 * Graceful states mirror the sibling tiles (WeatherTile / HomeStatusStrip):
 *  - loading       → pulse skeleton
 *  - error/no data → warm Norwegian fallback line (the briefing is non-critical;
 *                    the chore loop and calendar carry the real signal)
 *
 * The summary itself is already defended server-side (containsAllergen with a
 * deterministic fallback), so the client renders it as trusted plain text.
 *
 * #154 (grønt glass): et glasskort med lime ikonrund til venstre, etiketten
 * «Dagens hilsen» og teksten. Streak-linja under teksten er flyttet til
 * streak-chipen i toppen på Hjem, som skissen viser. Kortet er personlig og
 * vises bare med en innlogget bruker (OverviewRoute).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/primitives/Card';
import { Icon } from '../../../components/primitives/Icon';
import { useBriefing } from '../useBriefing';

function BriefingCard({ children }: { children: ReactNode }): ReactNode {
  const { t } = useTranslation();
  return (
    <section aria-label={t('briefing.title')}>
      <Card className="flex items-start gap-3.5 !p-4">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-fab text-on-fab"
        >
          <Icon.Sparkle size={20} />
        </span>
        <div className="min-w-0 flex-1">{children}</div>
      </Card>
    </section>
  );
}

export function BriefingTile(): ReactNode {
  const { t } = useTranslation();
  const { data, isLoading, error } = useBriefing();

  if (isLoading) {
    return (
      <BriefingCard>
        <div data-testid="briefing-loading" className="h-16 w-full animate-pulse rounded-md bg-inset" />
      </BriefingCard>
    );
  }

  if (error || !data) {
    return (
      <BriefingCard>
        <p className="pt-2.5 text-sm text-ink-2" data-testid="briefing-unavailable">
          {t('briefing.unavailable')}
        </p>
      </BriefingCard>
    );
  }

  return (
    <BriefingCard>
      <p
        data-testid="briefing-title"
        className="text-caption font-bold tracking-wider text-ink-2 uppercase"
      >
        {t('briefing.title')}
      </p>
      <p data-testid="briefing-summary" className="mt-1 text-body leading-snug font-medium text-ink">
        {data.summary}
      </p>
    </BriefingCard>
  );
}
